import React, { useState, useEffect } from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import {
  useForm,
  SubmitHandler,
  Controller,
  useWatch,
} from "react-hook-form";
import { StylesConfig } from "react-select";
import DurationSelect from "app/components/inputs/DurationSelect";
import CloseIcon from "../../../../../../public/images/close.svg";
import { useTranslation } from "react-i18next";

import styles from "../AddBookingOption/styles.module.scss";
import ButtonVariant from "app/components/Button/ButtonVariant";
import TextInput from "app/components/inputs/TextInput";
import CheckBox from "app/components/inputs/CheckBox";
import CalendarColorPicker from "app/components/inputs/CalendarColorPicker";
import { toPayloadColor } from "app/utils/calendarColors";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { LessonRatePayload } from "app/utils/request/scheduling/facilityLessonRates";
import ApplyScopeConfirmModal from "app/components/ApplyScopeConfirmModal";
import { parseDurationMinutes } from "app/utils/timeUtils";
import PayMethodSelector, {
  PaymentMethodValue,
  toPaymentPayload,
  fromPaymentModel,
} from "app/components/PayMethodSelector";
import { resourceIdsPayload } from "app/components/ResourceSelector";
import ResourcesBlock from "app/components/ResourceSelector/ResourcesBlock";
import {
  ScheduleResourceResponse,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import {
  ResourceSelection,
  EMPTY_RESOURCE_SELECTION,
} from "app/components/ResourceSelector/selection";


interface FormValues {
  title: string;
  duration: string;
  rate: number | string;
  location: string;
  description: string;
  capacity: number | string;
  defaultRate: boolean;
  paymentMethod: PaymentMethodValue;
  bookingsLimit: number | string;
  hidePrice: boolean;
  color: string | null;
}

interface EditBookingOptionProps {
  isOpen: boolean;
  onClose: () => void;
  item: LessonRateModel | null;
  onEditBooking: (
    rateId: number,
    data: Partial<LessonRatePayload>
  ) => Promise<void>;
  existingTitles: string[];
  currencySymbol: string;
  showBookingsLimit?: boolean;
  stripeIntegrationEnabled?: boolean;
  resources?: ScheduleResourceResponse[];
  categories?: ResourceCategoryResponse[];
  resourcesEnabled?: boolean;
}

const formatDuration = (minutes: number): string => `${minutes} mins`;

export default function EditBookingOption({
  isOpen,
  onClose,
  item,
  onEditBooking,
  existingTitles,
  currencySymbol,
  showBookingsLimit = false,
  stripeIntegrationEnabled,
  resources = [],
  categories = [],
  resourcesEnabled = false,
}: EditBookingOptionProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    control,
    trigger,
  } = useForm<FormValues>();

  const [titleError, setTitleError] = useState<string | null>(null);
  const { t } = useTranslation();

  // CN-2711: hiding the price only makes sense when the athlete never reaches
  // a checkout, so the option has to be collected offline and nothing else.
  const paymentMethod = useWatch({ control, name: "paymentMethod" });
  const hidePrice = useWatch({ control, name: "hidePrice" });
  const canHidePrice = (paymentMethod?.collectType ?? "offline") === "offline";

  useEffect(() => {
    if (!canHidePrice && hidePrice) {
      setValue("hidePrice", false);
    }
  }, [canHidePrice, hidePrice, setValue]);
  const [showPayments, setShowPayments] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [pendingPayload, setPendingPayload] = useState<Partial<LessonRatePayload> | null>(null);
  const [showApplyScope, setShowApplyScope] = useState(false);

  // Pre-fill form when item changes
  useEffect(() => {
    if (item) {
      setValue("title", item.title);
      setValue("duration", formatDuration(item.durationInMinutes));
      setValue("rate", item.rate || "");
      setValue("location", item.location || "");
      setValue("description", item.description || "");
      setValue("capacity", item.capacity || 1);
      setValue("defaultRate", item.defaultRate);
      setValue("bookingsLimit", item.bookingsLimit ?? "");
      setValue("hidePrice", !!item.hidePrice);
      setValue("color", item.color ?? null);
      setValue(
        "paymentMethod",
        fromPaymentModel({
          collectType: (item as any).collectType,
          onlineDefault: (item as any).onlineDefault,
          packageOnly: (item as any).packageOnly,
        })
      );
    }
  }, [item, setValue]);

  const [resourceSelection, setResourceSelection] = useState<ResourceSelection>(
    EMPTY_RESOURCE_SELECTION
  );

  // Reload the picker from the saved item on each open. A Cancel keeps the
  // unsaved pick in state, so a reopen must not show it.
  useEffect(() => {
    if (item && isOpen) {
      setResourceSelection({
        scheduleResourceIds: item.scheduleResourceIds ?? [],
        resourceCategoryIds: item.resourceCategoryIds ?? [],
      });
    }
  }, [item, isOpen]);

  // Drop transient apply-scope state whenever the edit modal closes, so a
  // dismissed edit never leaks its confirmation modal or a stale payload into
  // the next open.
  useEffect(() => {
    if (!isOpen) {
      setShowApplyScope(false);
      setPendingPayload(null);
    }
  }, [isOpen]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!item) { return; }

    setServerErrors({});
    setCapacityError(null);

    if (
      data.title !== item.title &&
      Array.isArray(existingTitles) &&
      existingTitles.some(
        (t) => t.toLowerCase() === data.title.toLowerCase()
      )
    ) {
      setTitleError(t("Title must be unique"));
      return;
    }

    const capacityVal = data.capacity !== "" ? Number(data.capacity) : 1;
    if (capacityVal < 1) {
      setCapacityError(t("Must be at least 1"));
      return;
    }

    const { collect_type, online_default } = toPaymentPayload(data.paymentMethod);
    const pickedColor = toPayloadColor(data.color);

    const payload: Partial<LessonRatePayload> = {
      title: data.title,
      duration: data.duration,
      rate: data.rate !== "" ? Number(data.rate) : 0,
      location: data.location || "",
      description: data.description?.trim() || null,
      capacity: capacityVal,
      default_rate: data.defaultRate,
      collect_type,
      online_default,
      bookings_limit:
        data.bookingsLimit !== "" ? Number(data.bookingsLimit) : null,
      // CN-2711: only send the flag when it actually asks for something. A
      // booking service that predates the column answers 500 to any payload
      // carrying hide_price, so an unrelated edit must not start failing
      // against an environment where this feature has not shipped yet.
      ...(!!data.hidePrice !== !!item?.hidePrice ? { hide_price: !!data.hidePrice } : {}),
      // Calendar colour coding takes the same guard, keyed on "changed" so
      // clearing a colour still sends the null that unsets it. Compared raw:
      // a legacy value outside the palette shows as None, and normalizing it
      // first would clear it on every unrelated save.
      ...((data.color ?? null) !== (item?.color ?? null) ? { color: pickedColor } : {}),
      ...resourceIdsPayload(
        resourcesEnabled,
        resourceSelection.scheduleResourceIds,
        resourceSelection.resourceCategoryIds
      ),
    };

    // Only ask about existing bookings when something that affects them changed.
    if (durationOrLocationChanged(payload)) {
      setPendingPayload(payload);
      setShowApplyScope(true);
      return;
    }

    await submitEdit(payload, false);
  };

  const durationOrLocationChanged = (payload: Partial<LessonRatePayload>) => {
    if (!item) {
      return false;
    }
    const after = parseDurationMinutes(payload.duration);
    const durationChanged =
      after !== null
        ? after !== item.durationInMinutes
        : payload.duration !== formatDuration(item.durationInMinutes);
    return durationChanged || (payload.location || "") !== (item.location || "");
  };

  const submitEdit = async (
    payload: Partial<LessonRatePayload>,
    applyToExisting: boolean,
  ) => {
    if (!item) { return; }

    try {
      await onEditBooking(item.id, { ...payload, apply_to_existing: applyToExisting });
      setShowApplyScope(false);
      setPendingPayload(null);
      reset();
      setTitleError(null);
      setShowPayments(false);
    } catch (err) {
      const error = err as any;
      if (error?.errors) {
        const fieldErrors: Record<string, string> = {};
        Object.keys(error.errors).forEach((key) => {
          const messages = error.errors[key];
          if (Array.isArray(messages) && messages.length > 0) {
            const camelKey = key.replace(/_([a-z])/g, (_: string, letter: string) => letter.toUpperCase());
            fieldErrors[camelKey] = messages[0].charAt(0).toUpperCase() + messages[0].slice(1);
          }
        });
        setServerErrors(fieldErrors);
      }
      setShowApplyScope(false);
      setPendingPayload(null);
    }
  };

  const onCancel = () => {
    onClose();
    reset();
    setShowPayments(false);
    setTitleError(null);
    setServerErrors({});
    setCapacityError(null);
  };

  const customStyles: StylesConfig = {
    control: (base: any) => ({
      ...base,
      background: "white",
      border: "1px solid #ccc",
    }),
    multiValue: (styles: any) => ({
      ...styles,
      backgroundColor: "#27a4ff",
      padding: "4px",
      borderRadius: "4px",
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      color: "white",
      fontSize: "16px",
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      color: "white",
      ":hover": {
        color: "white",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "black",
    }),
    container: (provided: any) => ({
      ...provided,
      color: "#8C8C8C",
    }),
    placeholder: (defaultStyles: any) => ({
      ...defaultStyles,
      color: "#9faac7",
    }),
  };

  const durationCustomStyles: StylesConfig = {
    ...customStyles,
    valueContainer: (base: any) => ({
      ...base,
      height: "42px",
      display: "flex",
      alignItems: "center",
    }),
  };

  useEffect(() => {
    register("duration", { required: true });
  }, [register]);

  return (
    <>
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <h2>{t("Edit Booking")}</h2>
        <img
          className={styles.modal_status_icon}
          src={CloseIcon}
          alt="close"
          onClick={onCancel}
        />
      </div>
      <div className={styles.modal_content}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_row}>
            <TextInput
              label={`${t("Title")}*`}
              placeholder={t("e.g. 1 Hour Adult Lesson")}
              {...register("title", { required: true })}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTitleError(null);
                setServerErrors((prev) => {
                  const next = { ...prev };
                  delete next.title;
                  return next;
                });
                register("title", { required: true }).onChange(e);
              }}
            />
            {errors.title && <span>{t("This field is required")}</span>}
            {titleError && <span>{titleError}</span>}
            {serverErrors.title && (
              <span style={{ color: "red" }}>{serverErrors.title}</span>
            )}
          </div>

          <div className={styles.form_row}>
            <label htmlFor="duration">{t("Duration")}*</label>
            <DurationSelect
              control={control}
              trigger={trigger}
              styles={durationCustomStyles}
            />
            {errors.duration && <span>{t("This field is required")}</span>}
          </div>

          <div className={styles.form_row}>
            <label htmlFor="rate">{t("Rate")}</label>
            <div className={styles.input_with_dollar}>
              <span className={styles.dollar_sign}>{currencySymbol}</span>
              <input
                id="rate"
                type="number"
                {...register("rate")}
                placeholder="0"
                onKeyPress={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "+") {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  const value = parseFloat(e.target.value);
                  if (value < 0 || isNaN(value)) {
                    setValue("rate", 0);
                    trigger("rate");
                  }
                }}
              />
            </div>
          </div>

          {/* CN-2711: the rate is still charged and reported, it just stops
              being shown to whoever is booking. Only offered when the option is
              collected offline only — any online payment means a checkout, and
              a checkout cannot hide what it charges. */}
          {canHidePrice && (
            <div className={styles.form_row_checkbox}>
              <Controller
                name="hidePrice"
                control={control}
                render={({ field }) => (
                  <CheckBox
                    name="hidePrice"
                    label={t("Hide price?")}
                    checked={!!field.value}
                    onChange={field.onChange}
                    squareCheckIcons
                    testId="hide-price-checkbox"
                  />
                )}
              />
            </div>
          )}

          <div className={styles.form_row}>
            <TextInput
              label={t("Location")}
              placeholder={t("Where the session takes place")}
              {...register("location")}
            />
          </div>

          <div className={styles.form_row}>
            <label htmlFor="description">
              {t("Description (shown on your landing page)")}
            </label>
            <textarea
              id="description"
              className={styles.textarea}
              {...register("description", { maxLength: 1000 })}
              placeholder={t("What this session covers, for whoever is booking it")}
              maxLength={1000}
              rows={3}
            />
          </div>


          {showBookingsLimit && (
            <div>
              <div className={styles.form_row}>
                <label htmlFor="capacity">{t("Capacity")}</label>
                <input
                  id="capacity"
                  type="number"
                  {...register("capacity")}
                  placeholder="1"
                  onKeyPress={(e) => {
                    if (["-", "e", "+", ".", ","].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value < 1) {
                      setCapacityError(t("Must be at least 1"));
                    } else {
                      setCapacityError(null);
                    }
                  }}
                  onChange={(e) => {
                    setCapacityError(null);
                    setServerErrors((prev) => {
                      const next = { ...prev };
                      delete next.capacity;
                      return next;
                    });
                    register("capacity").onChange(e);
                  }}
                />
              </div>
              {(capacityError || serverErrors.capacity) && (
                <span style={{ color: "red", fontSize: "14px" }}>
                  {capacityError || serverErrors.capacity}
                </span>
              )}
            </div>
          )}

          {showBookingsLimit && (
            <div className={styles.form_row}>
              <label htmlFor="bookingsLimit">{t("Bookings Limit")}</label>
              <input
                id="bookingsLimit"
                type="number"
                {...register("bookingsLimit", {
                  min: { value: 1, message: t("Must be at least 1") },
                })}
                placeholder={t("Unlimited")}
                onKeyPress={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "+" || e.key === ".") {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value < 1) {
                    setValue("bookingsLimit", "");
                    trigger("bookingsLimit");
                  }
                }}
              />
              <span className={styles.field_hint}>
                {t("Maximum number of future sessions an athlete can book")}
              </span>
              {errors.bookingsLimit && (
                <span style={{ color: "red" }}>{errors.bookingsLimit.message}</span>
              )}
              {serverErrors.bookingsLimit && (
                <span style={{ color: "red" }}>{serverErrors.bookingsLimit}</span>
              )}
            </div>
          )}

          <div className={styles.form_row}>
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <CalendarColorPicker
                  label={t("Calendar colour")}
                  hint={t("Sessions booked with this option appear in this colour on the calendar.")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className={styles.paymentDropdown}>
            <div
              className={styles.paymentToggle}
              onClick={() => setShowPayments(!showPayments)}
            >
              <div>
                <div className={styles.paymentsTitle}>{t("Payments")}</div>
                {!showPayments && (
                  <div className={styles.paymentSummary}>
                    {t("Online and Offline")}
                  </div>
                )}
              </div>
              <i
                className={`ico ico-chevron ${showPayments ? styles.open : ""}`}
              />
            </div>

            {showPayments && (
              <div className={styles.paymentsContent}>
                <p className={styles.subtext}>
                  {t(
                    "Please choose your preferred method for collecting payments for this booking option."
                  )}
                </p>

                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <PayMethodSelector
                      value={field.value}
                      onChange={field.onChange}
                      showPackageOnly
                      disableOnline={!stripeIntegrationEnabled}
                      disabledReason={!stripeIntegrationEnabled ? t("Connect Stripe to enable online payments") : undefined}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {resourcesEnabled && (
            <ResourcesBlock
              resources={resources}
              categories={categories}
              value={resourceSelection}
              onChange={setResourceSelection}
              blockStyles={styles}
            />
          )}

          <div className={styles.buttonGroup}>
            <ButtonVariant
              title={t("Cancel")}
              buttonType="secondary-outline"
              buttonStyles={styles.cancel_button}
              onClickFunc={onCancel}
            />
            <ButtonVariant
              title={t("Confirm")}
              buttonType="primary"
              buttonStyles={styles.confirm_button}
              onClickFunc={handleSubmit(onSubmit)}
            />
          </div>
        </form>
      </div>
    </ModalLayout>

      <ApplyScopeConfirmModal
        isOpen={showApplyScope}
        onAllUpcoming={() => pendingPayload && submitEdit(pendingPayload, true)}
        onNewOnly={() => pendingPayload && submitEdit(pendingPayload, false)}
        onCancel={() => {
          setShowApplyScope(false);
          setPendingPayload(null);
        }}
      />
    </>
  );
}
